import React, { useState } from "react";
import { Funnel } from "recharts";
import Icon from "components/AppIcon";

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload?.length) {
    const d = payload?.[0]?.payload;
    return (
      <div
        className="rounded-lg border p-3 text-sm"
        style={{ background: "var(--color-card)", borderColor: "var(--color-border)", boxShadow: "var(--shadow-lg)", minWidth: 160 }}
      >
        <p className="font-heading font-semibold mb-1" style={{ color: "var(--color-foreground)" }}>{d?.name}</p>
        <p className="font-data" style={{ color: "var(--color-primary)" }}>{d?.value?.toLocaleString()} customers</p>
        {d?.convRate && (
          <p className="font-caption text-xs mt-1" style={{ color: "var(--color-text-secondary)" }}>
            Conv. from prev: <span style={{ color: "#059669" }}>{d?.convRate}%</span>
          </p>
        )}
      </div>
    );
  }
  return null;
};

export default function CustomerJourneyFunnel({ funnelData: funnelDataProp, touchpointsMap }) {
  const [hoveredStage, setHoveredStage] = useState(null);
  const [selectedStage, setSelectedStage] = useState(null);

  const stages = (funnelDataProp || []).map((stage, index, arr) => {
    const prevValue = arr[index - 1]?.value;
    const fillColors = ["#DBEAFE", "#EDE9FE", "#FEF3C7", "#FCE7F3", "#D1FAE5", "#FEE2E2"];
    const strokeColors = ["#1E40AF", "#7C3AED", "#D97706", "#DB2777", "#059669", "#DC2626"];
    return {
      name: stage.name,
      value: stage.value,
      fill: fillColors[index] || "#DBEAFE",
      stroke: strokeColors[index] || "#1E40AF",
      icon: stage.icon || "Circle",
      convRate: stage.conversionRatePercent ?? (prevValue ? Number(((stage.value / prevValue) * 100).toFixed(1)) : null),
    };
  });

  const touchpointRows = touchpointsMap
    ? Object.entries(touchpointsMap).map(([stage, points], index) => ({
        stage,
        points,
        color: stages[index]?.stroke || "#1E40AF",
      }))
    : [];

  const overallConversion = stages.length > 1 && stages[0]?.value
    ? ((stages[stages.length - 1]?.value || 0) / stages[0].value) * 100
    : null;

  const activeStage = selectedStage || hoveredStage;
  const activeTouchpoints = touchpointRows?.find((t) => t?.stage === activeStage);

  return (
    <div
      className="rounded-xl border h-full"
      style={{ background: "var(--color-card)", borderColor: "var(--color-border)", boxShadow: "var(--shadow-md)", padding: "20px" }}
    >
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h3 className="font-heading font-semibold text-base lg:text-lg" style={{ color: "var(--color-foreground)" }}>
            Customer Journey Funnel
          </h3>
          <p className="font-caption text-xs mt-0.5" style={{ color: "var(--color-text-secondary)" }}>
            Touchpoint analysis &amp; stage conversion rates
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span
            className="font-caption text-xs px-2 py-1 rounded-full"
            style={{ background: "var(--color-muted)", color: "var(--color-text-secondary)" }}
          >
            Overall Conv: <strong style={{ color: "#059669" }}>{overallConversion == null ? "-" : `${overallConversion.toFixed(1)}%`}</strong>
          </span>
        </div>
      </div>
      <div className="flex flex-col lg:flex-row gap-4">
        {/* Funnel bars - custom horizontal */}
        <div className="flex-1 min-w-0">
          {stages?.map((stage, idx) => {
            const maxVal = stages?.[0]?.value;
            const widthPct = (stage?.value / maxVal) * 100;
            const isActive = activeStage === stage?.name;
            return (
              <div
                key={stage?.name}
                className="mb-2 cursor-pointer"
                onMouseEnter={() => setHoveredStage(stage?.name)}
                onMouseLeave={() => setHoveredStage(null)}
                onClick={() => setSelectedStage(selectedStage === stage?.name ? null : stage?.name)}
              >
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <Icon name={stage?.icon} size={14} color={stage?.stroke} />
                    <span
                      className="font-caption text-xs font-medium"
                      style={{ color: isActive ? stage?.stroke : "var(--color-foreground)" }}
                    >
                      {stage?.name}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    {stage?.convRate && (
                      <span className="font-data text-xs" style={{ color: "#059669" }}>
                        ↓{stage?.convRate}%
                      </span>
                    )}
                    <span className="font-data text-xs font-medium" style={{ color: "var(--color-foreground)" }}>
                      {stage?.value?.toLocaleString()}
                    </span>
                  </div>
                </div>
                <div
                  className="h-7 rounded-md transition-all duration-300 flex items-center px-2"
                  style={{
                    width: `${widthPct}%`,
                    minWidth: 60,
                    background: isActive ? stage?.stroke : stage?.fill,
                    border: `1.5px solid ${stage?.stroke}`,
                    opacity: activeStage && !isActive ? 0.5 : 1,
                  }}
                >
                  <span
                    className="font-caption text-xs font-semibold truncate"
                    style={{ color: isActive ? "#fff" : stage?.stroke }}
                  >
                    {((stage?.value / maxVal) * 100)?.toFixed(1)}%
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Touchpoints panel */}
        <div
          className="lg:w-48 xl:w-56 rounded-lg p-3 flex-shrink-0"
          style={{ background: "var(--color-surface-2)", border: "1px solid var(--color-border)" }}
        >
          <p className="font-caption text-xs font-semibold mb-2" style={{ color: "var(--color-text-secondary)" }}>
            {activeTouchpoints ? `${activeTouchpoints?.stage} Touchpoints` : "Hover a stage"}
          </p>
          {activeTouchpoints ? (
            <div className="flex flex-col gap-1.5">
              {activeTouchpoints?.points?.map((pt, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2 px-2 py-1.5 rounded-md"
                  style={{ background: "var(--color-card)", border: "1px solid var(--color-border)" }}
                >
                  <div
                    className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                    style={{ background: activeTouchpoints?.color }}
                  />
                  <span className="font-caption text-xs" style={{ color: "var(--color-foreground)" }}>
                    {pt}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col gap-1.5">
              {stages?.map((s) => (
                <div key={s?.name} className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full" style={{ background: s?.stroke }} />
                  <span className="font-caption text-xs" style={{ color: "var(--color-text-secondary)" }}>
                    {s?.name}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}